'use client';

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import Link from 'next/link';
import './enterpriseai.css';

/* All styling lives in enterpriseai.css (scoped to .eai-page).
   Images go in public/assets/images/ .
   "Enterprise AI in action" cards: har card ka image path neeche `useCases` array me `img` field me badlo.
   Links: `capabilities` aur `useCases` me `href` field me badlo. Jahan page nahi hai wahan '#contact' rakha hai. */

type IconName =
  | 'target' | 'brain' | 'chart' | 'nodes' | 'bot' | 'spark'
  | 'shield' | 'link' | 'gear' | 'arrow' | 'trend' | 'bolt'
  | 'users' | 'rocket' | 'compass' | 'check';

function Icon({ name }: { name: IconName }) {
  const p: Record<IconName, ReactNode> = {
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" /></>,
    brain: <><path d="M9 4a3 3 0 0 0-3 3 3 3 0 0 0-2 5 3 3 0 0 0 2 5 3 3 0 0 0 6 1V4a3 3 0 0 0-3 0z" /><path d="M15 4a3 3 0 0 1 3 3 3 3 0 0 1 2 5 3 3 0 0 1-2 5 3 3 0 0 1-6 1" /></>,
    chart: <><path d="M4 20V4M4 20h16" /><rect x="8" y="12" width="3" height="6" /><rect x="13" y="8" width="3" height="10" /></>,
    nodes: <><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="6" r="2.5" /><circle cx="18" cy="18" r="2.5" /><path d="m8.2 10.8 7.6-3.6M8.2 13.2l7.6 3.6" /></>,
    bot: <><rect x="5" y="8" width="14" height="11" rx="2" /><path d="M12 8V4M9 13h.01M15 13h.01M9 16h6" /></>,
    spark: <><path d="M12 3c.6 5 3 7.4 8 9-5 1.6-7.4 4-8 9-.6-5-3-7.4-8-9 5-1.6 7.4-4 8-9z" /></>,
    shield: <><path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" /><path d="m9 12 2 2 4-4" /></>,
    link: <><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></>,
    gear: <><circle cx="12" cy="12" r="3" /><circle cx="12" cy="12" r="8" /></>,
    arrow: <><path d="M5 12h14M13 6l6 6-6 6" /></>,
    trend: <><path d="M4 17 9 11l4 3 7-8" /><path d="M15 6h5v5" /></>,
    bolt: <><path d="M13 3 5 13h6l-1 8 8-10h-6z" /></>,
    users: <><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M16 5a3 3 0 0 1 0 6M18 14c1.8.8 3 2.6 3 5" /></>,
    rocket: <><path d="M12 3c4 1 6 5 5 10l-3 3-5-5 3-3c-1-2-1-4 0-5z" /><path d="M9 14l-4 1 1-4M10 18l-1 4 3-2" /></>,
    compass: <><circle cx="12" cy="12" r="9" /><path d="m15.5 8.5-2 5-5 2 2-5z" /></>,
    check: <><circle cx="12" cy="12" r="9" /><path d="m8.5 12.5 2.5 2.5 4.5-5" /></>,
  };
  return (
    <svg className="ea-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {p[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

const stages = ['Strategy', 'Build', 'Deploy', 'Scale', 'Govern'];

const proofPoints = [
  'Business context, not just technology',
  'Real enterprise experience',
  'Practical, measurable outcomes',
  'People + AI, working together',
];

/* href: card ka "Learn more" link. '#contact' = abhi is card ka alag page nahi hai. */
const capabilities: { icon: IconName; title: string; text: string; href: string }[] = [
  { icon: 'target', title: 'AI Strategy & Use Cases', text: 'Identify high-value use cases aligned to business goals and build a clear roadmap for adoption.', href: '#contact' },
  { icon: 'brain', title: 'Machine Learning', text: 'Build and deploy supervised, unsupervised and deep learning models for real business problems.', href: '/whatwedo/Ai-and-Intelligence/machine-learning' },
  { icon: 'chart', title: 'Predictive Intelligence', text: 'Anticipate what is next with data-driven predictions to improve decisions and outcomes.', href: '/whatwedo/Ai-and-Intelligence/predictive-intelligence' },
  { icon: 'nodes', title: 'Decision Intelligence', text: 'Recommend the best actions using AI, business rules and real-time data.', href: '/whatwedo/Ai-and-Intelligence/decision-intelligence' },
  { icon: 'bot', title: 'AI Agents', text: 'Build agentic AI solutions that can perceive, reason and take action across enterprise systems.', href: '/whatwedo/Ai-and-Intelligence/ai-agent' },
  { icon: 'spark', title: 'ARIA', text: 'Our AI framework to accelerate enterprise adoption with reusable components and governance.', href: '#contact' },
];

const solutions: { icon: IconName; title: string; text: string }[] = [
  { icon: 'compass', title: 'Enterprise AI Advisory', text: 'Use case identification, feasibility and roadmap' },
  { icon: 'brain', title: 'Model Development & Training', text: 'Custom model building and fine-tuning' },
  { icon: 'bot', title: 'AI Agent Development', text: 'Autonomous agents for business processes' },
  { icon: 'spark', title: 'ARIA Framework', text: 'Accelerators, tooling and governance' },
  { icon: 'link', title: 'Integration with SAP & Other Systems', text: 'Connect AI with ERP, CRM, SFA and enterprise data' },
  { icon: 'shield', title: 'Responsible AI', text: 'Ethical, secure and compliant AI adoption' },
];

const approach = [
  { title: 'Discover', text: 'Understand business context, challenges and data readiness.' },
  { title: 'Define', text: 'Prioritise use cases and define success metrics.' },
  { title: 'Build', text: 'Develop and train models or agents with real data.' },
  { title: 'Deploy', text: 'Integrate with enterprise systems and processes.' },
  { title: 'Scale', text: 'Drive adoption, measure value and continuously improve.' },
];

const values: { icon: IconName; label: string }[] = [
  { icon: 'shield', label: 'Higher productivity' },
  { icon: 'bolt', label: 'Faster decision-making' },
  { icon: 'nodes', label: 'Improved forecast accuracy' },
  { icon: 'users', label: 'Enhanced customer experience' },
  { icon: 'gear', label: 'Optimised operations' },
  { icon: 'trend', label: 'New revenue opportunities' },
];

/* img = full path from public/ folder (extension ke saath). Har card ke liye alag file daalo.
   href = "See how we help" link. '#contact' = abhi page nahi hai. */
const useCases: { title: string; text: string; img: string; href: string }[] = [
  {
    title: 'Demand Forecasting',
    text: 'Improve forecast accuracy in supply chain and manufacturing.',
    img: '/assets/images/supply-chain1.jpg',
    href: '/solutions/inventory-intelligence',
  },
  {
    title: 'Intelligent Customer Support',
    text: 'Automate and enhance customer service with AI agents.',
    img: '/assets/images/customer.jpg',
    href: '/solutions/customer',
  },
  {
    title: 'Finance Risk Prediction',
    text: 'Predict risk, detect anomalies and improve financial planning.',
    img: '/assets/images/finance.jpg',
    href: '/solutions/cash-working-capital',
  },
  {
    title: 'Operational Optimisation',
    text: 'Use AI to optimise maintenance, inventory and resource allocation.',
    img: '/assets/images/asset-downtime.jpg',
    href: '/solutions/asset-intelligence',
  },
  {
    title: 'Talent & Workforce Insights',
    text: 'Analyse workforce data to improve hiring, retention and productivity.',
    img: '/assets/images/Better-Workforce-Planning.jpg',
    href: '/solutions/people-intelligence',
  },
  {
    title: 'Sustainability & ESG',
    text: 'Use AI to track, predict and improve sustainability outcomes.',
    img: '/assets/images/sustainability-esg.jpg',
    href: '#contact',
  },
];

const whyOrane: { icon: IconName; title: string; text: string }[] = [
  { icon: 'target', title: 'Business-first approach', text: 'We start with outcomes, not just technology.' },
  { icon: 'link', title: 'End-to-end capabilities', text: 'From strategy to production and scale.' },
  { icon: 'nodes', title: 'Enterprise data expertise', text: 'Deep experience with SAP, CRM, SFA and other systems.' },
  { icon: 'shield', title: 'Responsible and secure AI', text: 'Built-in governance, compliance and ethics.' },
  { icon: 'check', title: 'Proven delivery experience', text: 'Real-world use cases and measurable impact.' },
  { icon: 'rocket', title: 'Continuous value realisation', text: 'We help you scale and keep improving.' },
];

/* ===================== SCROLL REVEAL ===================== */
/* One fade-up on section content. Content stays visible if JS or IntersectionObserver is missing. */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('rv--in');
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    root.querySelectorAll<HTMLElement>('.sec-head, .cap, .step, .val, .uc-card, .why').forEach((n, i) => {
      n.classList.add('rv');
      n.style.setProperty('--rv-delay', `${(i % 6) * 60}ms`);
      io.observe(n);
    });
    return () => io.disconnect();
  }, []);
  return ref;
}

/* ============================== PAGE ============================== */

export default function EnterpriseAI() {
  const ref = useReveal();

  return (
    <div className="eai-page" ref={ref}>
      <main>
        {/* HERO */}
        <section className="ea-hero">
          <div className="ea-hero-bg"></div>
          <div className="ea-hero-overlay"></div>
          <div className="op-container ea-hero-inner">
            <div className="ea-hero-content">
              <h1 className="ea-hero-title">
                Turn AI Possibilities <span>Into Real Business Outcomes.</span>
              </h1>
              <p className="ea-hero-text">
                Orane helps organisations build and scale Enterprise AI, from strategy and use cases to
                models, agents and enterprise-wide adoption, to drive measurable value across your business.
              </p>
              <div className="ea-actions">
                <Link className="btn-primary" href="/tellusyourproblem#tell-details">Tell Us Your AI Ambition</Link>
                <Link className="btn-outline" href="#capabilities">Explore Enterprise AI</Link>
              </div>
            </div>

          </div>
        </section>

        {/* PROOF STRIP */}
        <section className="ea-proof">
          <div className="op-container">
            <ul className="proof-list">
              {proofPoints.map((t) => (
                <li key={t}>
                  <Icon name="check" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CAPABILITIES: joined hairline grid */}
        <section className="op-section" id="capabilities">
          <div className="op-container">
            <div className="sec-head">
              <h2 className="sec-title">Our Enterprise AI capabilities</h2>
              <p className="sec-sub">From strategy to execution, we help you unlock the full potential of AI across your enterprise.</p>
            </div>
            <div className="cap-grid">
              {capabilities.map((c) => (
                <article className="cap" key={c.title}>
                  <Icon name={c.icon} />
                  <h3 className="card-title">{c.title}</h3>
                  <p className="card-text">{c.text}</p>
                  <Link className="text-link" href={c.href}>
                    Learn more <Icon name="arrow" />
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* AI THAT WORKS IN THE REAL WORLD */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="real-grid">
              <div className="real-copy sec-head">
                <h2 className="sec-title">AI that works in the real world.</h2>
                <p className="sec-sub">
                  We combine business understanding, data expertise and AI engineering to deliver
                  practical, scalable and responsible AI solutions.
                </p>
              </div>
              <div className="real-photo" role="img" aria-label="Analyst reviewing AI dashboards on multiple screens"></div>
              <div className="real-list">
                <h3 className="real-list-title">Our solutions include</h3>
                {solutions.map((s) => (
                  <div className="sol-row" key={s.title}>
                    <span className="sol-icon"><Icon name={s.icon} /></span>
                    <div>
                      <strong>{s.title}</strong>
                      <small>{s.text}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* APPROACH: horizontal timeline */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="sec-head">
              <h2 className="sec-title">From idea to impact: our AI approach</h2>
              <p className="sec-sub">We follow a structured approach to ensure AI delivers measurable and sustainable value.</p>
            </div>
            <ol className="timeline">
              {approach.map((a, i) => (
                <li className="step" key={a.title}>
                  <span className="step-dot">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="card-title">{a.title}</h3>
                  <p className="card-text">{a.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* MEASURABLE VALUE: stat-style strip */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="sec-head">
              <h2 className="sec-title">Create measurable value with Enterprise AI</h2>
              <p className="sec-sub">AI helps you do more with your data, people and processes.</p>
            </div>
            <div className="val-grid">
              {values.map((v) => (
                <div className="val" key={v.label}>
                  <Icon name={v.icon} />
                  <span>{v.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* IN ACTION */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="sec-head">
              <h2 className="sec-title">Enterprise AI in action</h2>
              <p className="sec-sub">Practical use cases across functions and industries.</p>
            </div>
            <div className="uc-grid">
              {useCases.map((u) => (
                <article className="card uc-card" key={u.title}>
                  <div className="uc-img" style={{ background: '#f4f8fd' }}>
                    <img src={u.img} alt={u.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block', maxWidth: 'none' }} />
                  </div>
                  <div className="uc-body">
                    <h3 className="card-title">{u.title}</h3>
                    <p className="card-text">{u.text}</p>
                    <Link className="text-link" href={u.href}>
                      See how we help <Icon name="arrow" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* WHY ORANE */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="sec-head">
              <h2 className="sec-title">Why Orane for Enterprise AI?</h2>
              <p className="sec-sub">We bring together domain expertise, data and AI capabilities to deliver real business value.</p>
            </div>
            <div className="why-grid">
              {whyOrane.map((w) => (
                <div className="why" key={w.title}>
                  <Icon name={w.icon} />
                  <div>
                    <h3 className="card-title">{w.title}</h3>
                    <p className="card-text">{w.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="ea-cta" id="contact">
          <div className="ea-cta-bg"></div>
          <div className="ea-cta-overlay"></div>
          <div className="op-container ea-cta-inner">
            <h2 className="ea-cta-title">Let&rsquo;s talk about your AI opportunity.</h2>
            <p className="ea-cta-text">
              Tell us where you are in your AI journey. We&rsquo;ll help you identify the right use cases,
              build a plan and deliver measurable outcomes.
            </p>
            <Link className="btn-primary" href="/tellusyourproblem#tell-details">Tell Us Your AI Ambition</Link>
          </div>
        </section>
      </main>
    </div>
  );
}